import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HardHat, CurrencyDollar, Clock, CheckCircle, XCircle, ArrowsClockwise, ChatText, PaperPlaneTilt, CaretDown, CaretUp, FileText, MapPin, Eye, DownloadSimple, CalendarBlank, Phone, EnvelopeSimple, Buildings, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending', cls: 'hb-badge-pending' },
  accepted: { label: 'Accepted', cls: 'hb-badge-accepted' },
  rejected: { label: 'Denied', cls: 'hb-badge-rejected' },
  changes_requested: { label: 'Changes Requested', cls: 'hb-badge-pending_signatures' },
  withdrawn: { label: 'Withdrawn', cls: 'hb-badge-completed' },
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const BidMessageThread = ({ bidId, userId }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    fetchMessages();
  }, [bidId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/bids/${bidId}/messages`);
      setMessages(res.data);
    } catch { /* ignore */ }
  };

  const handleSend = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/bids/${bidId}/messages`, { message: newMsg.trim() });
      setNewMsg('');
      fetchMessages();
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto space-y-3 p-4 min-h-[200px] max-h-[360px]" style={{ background: 'var(--bg-deep)', borderRadius: '8px' }}>
        {messages.length === 0 && (
          <p className="text-center text-sm py-8" style={{ color: 'var(--text-muted)' }}>No messages yet. Start the conversation.</p>
        )}
        {messages.map(m => {
          const isMe = m.sender_id === userId;
          return (
            <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[80%] px-3 py-2 rounded-lg text-sm" style={{
                background: isMe ? 'rgba(201,206,211,0.15)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${isMe ? 'rgba(201,206,211,0.2)' : 'rgba(255,255,255,0.08)'}`,
                color: 'var(--text-primary)',
              }}>
                <div className="text-xs font-semibold mb-1" style={{ color: isMe ? 'var(--gold)' : 'var(--cyan)' }}>
                  {m.sender_name} ({m.sender_role})
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{m.message}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{timeAgo(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-3">
        <Textarea
          data-testid="bid-message-input"
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }}}
          placeholder="Type a message..."
          rows={2}
          className="flex-1 hb-input text-sm"
        />
        <Button
          data-testid="bid-message-send"
          onClick={handleSend}
          disabled={sending || !newMsg.trim()}
          className="btn-gold self-end"
          size="sm"
        >
          <PaperPlaneTilt size={18} weight="bold" />
        </Button>
      </div>
    </div>
  );
};

const BidManagement = ({ userId, onProjectDragStart, onDragEnd, onBidTrashed }) => {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);
  const [actionBid, setActionBid] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [changeMsg, setChangeMsg] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [messageBid, setMessageBid] = useState(null);
  const [viewBid, setViewBid] = useState(null);

  useEffect(() => { fetchBids(); }, []);

  const downloadAttachment = async (bidId, filePath, filename) => {
    try {
      const r = await axios.get(`${API}/bids/${bidId}/download-attachment?file_path=${encodeURIComponent(filePath)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  const fetchBids = async () => {
    try {
      const res = await axios.get(`${API}/owner/bids`);
      setBids(res.data);
    } catch { toast.error('Failed to load bids'); }
    finally { setLoading(false); }
  };

  const handleAccept = async (bid) => {
    if (!window.confirm(`Accept ${bid.gc_name}'s bid of $${bid.amount.toLocaleString()} for "${bid.project_title}"?\n\nThis will award the project and reject other bids.`)) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/bids/${bid.id}/accept`);
      toast.success('Bid accepted! Project awarded.');
      fetchBids();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to accept bid'); }
    finally { setProcessing(false); }
  };

  const handleDeny = async () => {
    if (!actionBid) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/bids/${actionBid.id}/deny`, { reason: denyReason });
      toast.success('Bid denied');
      setActionBid(null);
      setActionType(null);
      setDenyReason('');
      fetchBids();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to deny bid'); }
    finally { setProcessing(false); }
  };

  const handleRequestChanges = async () => {
    if (!actionBid || !changeMsg.trim()) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/bids/${actionBid.id}/request-changes`, { message: changeMsg.trim() });
      toast.success('Change request sent to contractor');
      setActionBid(null);
      setActionType(null);
      setChangeMsg('');
      fetchBids();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to request changes'); }
    finally { setProcessing(false); }
  };

  const handleDelete = async (bid) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/bids/${bid.id}/trash`);
      toast.success('Bid moved to Trash');
      fetchBids();
      if (onBidTrashed) onBidTrashed();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to trash bid'); }
    finally { setProcessing(false); }
  };

  // Group bids by project
  const groupedBids = bids.reduce((acc, bid) => {
    if (!acc[bid.project_id]) acc[bid.project_id] = { title: bid.project_title, location: bid.project_location, bids: [] };
    acc[bid.project_id].bids.push(bid);
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Loading bids...</div>;
  }

  if (bids.length === 0) {
    return (
      <div className="hb-empty p-10 text-center">
        <HardHat size={56} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
        <h3 className="text-lg font-semibold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>No Bids Yet</h3>
        <p style={{ color: 'var(--text-secondary)' }}>When contractors submit bids on your projects, they'll appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bid-management-section">
      {Object.entries(groupedBids).map(([projectId, group]) => {
        const isExpanded = expandedProject === projectId || expandedProject === null;
        const pendingCount = group.bids.filter(b => b.status === 'pending' || b.status === 'changes_requested').length;
        return (
          <div key={projectId} className="hb-card overflow-hidden">
            <button
              data-testid={`bid-project-toggle-${projectId.slice(0, 8)}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('project-id', projectId);
                e.dataTransfer.effectAllowed = 'move';
                if (onProjectDragStart) onProjectDragStart(projectId);
              }}
              onDragEnd={() => { if (onDragEnd) onDragEnd(); }}
              onClick={() => setExpandedProject(expandedProject === projectId ? '__none__' : projectId)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              style={{ background: 'rgba(26,31,36,0.6)', cursor: 'grab' }}
            >
              <div>
                <h3 className="text-base font-semibold font-heading" style={{ color: 'var(--gold-light)' }}>{group.title}</h3>
                {group.location && <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: 'var(--text-muted)' }}><MapPin size={12} weight="fill" />{group.location}</p>}
              </div>
              <div className="flex items-center gap-3">
                {pendingCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>
                    {pendingCount} pending
                  </span>
                )}
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{group.bids.length} bid{group.bids.length !== 1 ? 's' : ''}</span>
                {isExpanded ? <CaretUp size={16} style={{ color: 'var(--text-muted)' }} /> : <CaretDown size={16} style={{ color: 'var(--text-muted)' }} />}
              </div>
            </button>
            {isExpanded && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {group.bids.map(bid => {
                  const cfg = STATUS_CONFIG[bid.status] || STATUS_CONFIG.pending;
                  return (
                    <div key={bid.id} data-testid={`bid-row-${bid.id.slice(0, 8)}`}
                      draggable onDragStart={(e) => { e.dataTransfer.setData('bid-id', bid.id); e.dataTransfer.effectAllowed = 'move'; }}
                      className="hb-card overflow-hidden hb-lift"
                      style={{ cursor: 'grab' }}>
                      {/* Top — GC Identity */}
                      <div className="h-24 flex items-center justify-center relative" style={{ background: 'var(--bg-elevated)' }}>
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.15)' }}>
                          <HardHat size={28} weight="duotone" style={{ color: 'var(--cyan)' }} />
                        </div>
                        <span className={`hb-badge ${cfg.cls} absolute top-3 right-3`}>{cfg.label}</span>
                      </div>

                      {/* Body */}
                      <div className="p-5 cursor-pointer" onClick={() => setViewBid(bid)} data-testid={`open-bid-${bid.id.slice(0, 8)}`}>
                        <h3 className="text-base font-semibold font-heading mb-0.5 line-clamp-1" style={{ color: 'var(--text-primary)' }}>{bid.gc_name}</h3>
                        {bid.gc_company && <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{bid.gc_company}</p>}
                        <div className="space-y-2 mb-3">
                          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            <CurrencyDollar size={16} weight="duotone" style={{ color: 'var(--gold)' }} />
                            <span className="font-bold" style={{ color: 'var(--gold-light)' }}>${bid.amount.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            <Clock size={16} weight="duotone" /> {bid.estimated_duration || '—'}
                          </div>
                        </div>
                        {bid.proposal && <p className="text-xs line-clamp-2 mb-2 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{bid.proposal}</p>}
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{timeAgo(bid.submitted_at)}</span>
                      </div>

                      {/* Bottom Bar */}
                      <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-1.5" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {(bid.status === 'pending' || bid.status === 'changes_requested') && (
                            <>
                              <Button data-testid={`accept-bid-${bid.id.slice(0, 8)}`} onClick={() => handleAccept(bid)} disabled={processing} size="sm"
                                className="text-[10px] gap-1 h-7 px-2.5" style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}>
                                <CheckCircle size={12} weight="bold" /> Accept
                              </Button>
                              <Button data-testid={`deny-bid-${bid.id.slice(0, 8)}`} onClick={() => { setActionBid(bid); setActionType('deny'); }} disabled={processing} size="sm"
                                className="text-[10px] gap-1 h-7 px-2.5" style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' }}>
                                <XCircle size={12} weight="bold" /> Deny
                              </Button>
                              <Button data-testid={`request-changes-${bid.id.slice(0, 8)}`} onClick={() => { setActionBid(bid); setActionType('changes'); }} disabled={processing} size="sm"
                                className="text-[10px] gap-1 h-7 px-2.5" style={{ background: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)' }}>
                                <ArrowsClockwise size={12} weight="bold" /> Changes
                              </Button>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Button data-testid={`message-bid-${bid.id.slice(0, 8)}`} onClick={() => setMessageBid(bid)} size="sm" className="text-[10px] gap-1 h-7 px-2.5 btn-ghost">
                            <ChatText size={12} weight="bold" /> Message
                          </Button>
                          <button data-testid={`delete-bid-${bid.id.slice(0, 8)}`} onClick={() => handleDelete(bid)} disabled={processing}
                            className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-muted)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}>
                            <Trash size={14} weight="duotone" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Deny Dialog */}
      <Dialog open={actionType === 'deny'} onOpenChange={(open) => { if (!open) { setActionType(null); setActionBid(null); setDenyReason(''); } }}>
        <DialogContent style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: 'var(--gold-light)' }}>Deny Bid</DialogTitle>
          </DialogHeader>
          {actionBid && (
            <div className="space-y-4 mt-2">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Deny <strong>{actionBid.gc_name}</strong>'s bid of <strong>${actionBid.amount.toLocaleString()}</strong> for "{actionBid.project_title}"?
              </p>
              <Textarea
                data-testid="deny-reason-input"
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                placeholder="Reason for denial (optional)..."
                rows={3}
                className="hb-input"
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setActionType(null); setDenyReason(''); }} className="flex-1 btn-ghost">Cancel</Button>
                <Button onClick={handleDeny} disabled={processing} className="flex-1" style={{ background: 'rgba(239,68,68,0.8)', color: '#fff' }}>
                  {processing ? 'Denying...' : 'Confirm Deny'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Changes Dialog */}
      <Dialog open={actionType === 'changes'} onOpenChange={(open) => { if (!open) { setActionType(null); setActionBid(null); setChangeMsg(''); } }}>
        <DialogContent style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: 'var(--gold-light)' }}>Request Changes</DialogTitle>
          </DialogHeader>
          {actionBid && (
            <div className="space-y-4 mt-2">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Request changes on <strong>{actionBid.gc_name}</strong>'s bid for "{actionBid.project_title}".
              </p>
              <Textarea
                data-testid="change-request-input"
                value={changeMsg}
                onChange={(e) => setChangeMsg(e.target.value)}
                placeholder="Describe what changes or clarifications you need..."
                rows={4}
                className="hb-input"
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setActionType(null); setChangeMsg(''); }} className="flex-1 btn-ghost">Cancel</Button>
                <Button onClick={handleRequestChanges} disabled={processing || !changeMsg.trim()} className="flex-1 btn-gold">
                  {processing ? 'Sending...' : 'Send Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Message Thread Dialog */}
      <Dialog open={!!messageBid} onOpenChange={(open) => { if (!open) setMessageBid(null); }}>
        <DialogContent className="max-w-lg" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: 'var(--gold-light)' }}>
              Messages — {messageBid?.gc_name}
            </DialogTitle>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{messageBid?.project_title}</p>
          </DialogHeader>
          {messageBid && <BidMessageThread bidId={messageBid.id} userId={userId} />}
        </DialogContent>
      </Dialog>

      {/* View Bid Detail Dialog */}
      <Dialog open={!!viewBid} onOpenChange={(open) => { if (!open) setViewBid(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}>
          {viewBid && (() => {
            const cfg = STATUS_CONFIG[viewBid.status] || STATUS_CONFIG.pending;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl flex items-center gap-2" style={{ color: 'var(--gold-light)' }}>
                    <HardHat size={22} weight="duotone" style={{ color: 'var(--cyan)' }} /> Bid Details
                  </DialogTitle>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{viewBid.project_title}</p>
                </DialogHeader>
                <div className="mt-4 space-y-5">
                  {/* Status + Contractor info */}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{viewBid.gc_name}</h3>
                      {viewBid.gc_company && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{viewBid.gc_company}</p>}
                    </div>
                    <span className={`hb-badge ${cfg.cls}`}>{cfg.label}</span>
                  </div>

                  {/* Key figures */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                      <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--text-muted)' }}><CurrencyDollar size={13} /> Bid Amount</div>
                      <div className="text-xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>${viewBid.amount?.toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                      <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--text-muted)' }}><Clock size={13} /> Duration</div>
                      <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{viewBid.estimated_duration}</div>
                    </div>
                    <div className="rounded-lg p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                      <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--text-muted)' }}><CalendarBlank size={13} /> Submitted</div>
                      <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{new Date(viewBid.submitted_at).toLocaleDateString()}</div>
                    </div>
                  </div>

                  {/* Proposal / Description */}
                  {viewBid.proposal && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Bid Description / Scope</h4>
                      <div className="rounded-lg p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                        <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>{viewBid.proposal}</p>
                      </div>
                    </div>
                  )}

                  {/* Change request message */}
                  {viewBid.change_request_message && (
                    <div className="rounded-lg px-4 py-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                      <div className="text-xs font-semibold mb-1" style={{ color: '#fbbf24' }}>Changes Requested:</div>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{viewBid.change_request_message}</p>
                    </div>
                  )}

                  {/* Attachments / Documents */}
                  {viewBid.attachments && viewBid.attachments.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Bid Documents ({viewBid.attachments.length})</h4>
                      <div className="space-y-2">
                        {viewBid.attachments.map((att, i) => (
                          <div key={i} className="flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText size={20} weight="duotone" style={{ color: 'var(--gold)' }} />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{att.filename}</p>
                              </div>
                            </div>
                            <button data-testid={`download-att-${i}`} onClick={() => downloadAttachment(viewBid.id, att.path, att.filename)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                              style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.15)', color: 'var(--gold)' }}>
                              <DownloadSimple size={14} weight="bold" /> Download
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Project info */}
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Project</h4>
                    <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{viewBid.project_title}</span>
                      {viewBid.project_location && <span className="flex items-center gap-1"><MapPin size={13} /> {viewBid.project_location}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    {(viewBid.status === 'pending' || viewBid.status === 'changes_requested') && (
                      <>
                        <Button data-testid="detail-accept-bid" onClick={() => { handleAccept(viewBid); setViewBid(null); }} disabled={processing} size="sm" className="text-xs gap-1" style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}><CheckCircle size={14} weight="bold" /> Accept</Button>
                        <Button data-testid="detail-deny-bid" onClick={() => { setActionBid(viewBid); setActionType('deny'); setViewBid(null); }} size="sm" className="text-xs gap-1" style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' }}><XCircle size={14} weight="bold" /> Deny</Button>
                        <Button data-testid="detail-request-changes" onClick={() => { setActionBid(viewBid); setActionType('changes'); setViewBid(null); }} size="sm" className="text-xs gap-1" style={{ background: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)' }}><ArrowsClockwise size={14} weight="bold" /> Request Changes</Button>
                      </>
                    )}
                    <Button data-testid="detail-message-bid" onClick={() => { setMessageBid(viewBid); setViewBid(null); }} size="sm" className="text-xs gap-1 btn-ghost"><ChatText size={14} weight="bold" /> Message GC</Button>
                    <Button data-testid="detail-delete-bid" onClick={() => { handleDelete(viewBid); setViewBid(null); }} disabled={processing} size="sm" className="text-xs gap-1" style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', border: '1px solid rgba(239,68,68,0.15)' }}><Trash size={14} weight="bold" /> Delete</Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BidManagement;
